/**
 * PulseDashboard — landing for `tru-pulse/__home__`.
 *
 * Renders the unread alerts feed (Required Action / Today's Tasks / Client
 * Insights / Portfolio Health / Latest News sections). Mirrors the Session
 * 32 Pulse app implementation.
 *
 * Each alert tile is clickable — clicking opens the alert in a Focus-style
 * chat tab in the prototype. In the React port the click handler is wired
 * to a callback so the host can route however it wants.
 */

import React from 'react';
import { useT } from '../../i18n/I18nProvider';

interface PulseAlert {
  id: string;
  category: 'required-action' | 'today-task' | 'client-insight' | 'health' | 'news';
  title: string;
  body: string;
  engagement?: string;
  unread?: boolean;
}

const ALERTS: PulseAlert[] = [
  {
    id: 'p-1',
    category: 'required-action',
    title: 'AlphaGo FY28 Timeline Change',
    body: 'Onsite dates shifted by 3 weeks. Review impact on staffing and deliverables.',
    engagement: 'AlphaGo FY28',
    unread: true,
  },
  {
    id: 'p-2',
    category: 'required-action',
    title: 'Review Revenue Cutoff Memo',
    body: 'Pink Garments memo ready for partner sign-off.',
    engagement: 'Pink Garments FY29',
    unread: true,
  },
  {
    id: 'p-3',
    category: 'today-task',
    title: 'GlobalSteel — Confirmations follow-up',
    body: '3 outstanding bank confirmations need a second-request email.',
    engagement: 'GlobalSteel FY28',
  },
  {
    id: 'p-4',
    category: 'client-insight',
    title: 'NovaTech Q4 earnings released',
    body: 'Revenue beat consensus by 4%. Materiality assessment may need refresh.',
    engagement: 'NovaTech FY28',
  },
];

// Category labels resolved at render time via t() so a language switch
// updates the eyebrow on every tile without remounting.
const CATEGORY_KEYS: Record<PulseAlert['category'], string> = {
  'required-action': 'pulse.category.requiredAction',
  'today-task': 'pulse.category.todayTask',
  'client-insight': 'pulse.category.clientInsight',
  health: 'pulse.category.portfolioHealth',
  news: 'pulse.category.latestNews',
};

interface PulseDashboardProps {
  onAlertClick?: (alertId: string) => void;
}

export const PulseDashboard = React.memo(function PulseDashboard({ onAlertClick }: PulseDashboardProps) {
  const t = useT();
  const unreadCount = ALERTS.filter(a => a.unread).length;
  return (
    <div className="ws-dash ws-dash--visible" id="pulseDashboard">
      <div className="ws-dash__scroll">
        <div className="ws-dash__grid">
          <header className="ws-dash__status-header">
            <h1 className="ws-dash__status-title">{t('dash.pulse.title')}</h1>
            <span className="ws-dash__status-desc">
              {unreadCount} {t('dash.pulse.unread')}
            </span>
          </header>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-12)' }}>
            {ALERTS.map(alert => (
              <button
                key={alert.id}
                type="button"
                className="body-card-grid__card"
                style={{
                  textAlign: 'left',
                  width: '100%',
                  cursor: 'pointer',
                  background: alert.unread ? 'var(--primary-cobalt-blue-5)' : 'var(--neutral-light-0)',
                  position: 'relative',
                }}
                onClick={() => onAlertClick?.(alert.id)}
              >
                {alert.unread && (
                  <span
                    aria-label="Unread"
                    style={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'var(--primary-cobalt-blue)',
                    }}
                  />
                )}
                <div style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', color: 'var(--neutral-light-60)', fontWeight: 700, letterSpacing: 'var(--ls-wide)' }}>
                  {t(CATEGORY_KEYS[alert.category])}
                  {alert.engagement && <> · {alert.engagement}</>}
                </div>
                <h3 className="body-card-grid__card-title" style={{ marginTop: 'var(--space-8)' }}>
                  {alert.title}
                </h3>
                <p className="body-card-grid__card-desc">{alert.body}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

export default PulseDashboard;
